import { useInstantSearch } from 'react-instantsearch';
import { defineMessages } from 'react-intl';
import styled, { createGlobalStyle } from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { SearchLayoutContainer } from '@sorare/core/src/components/search/SearchLayout/ui';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useToggle from '@sorare/core/src/hooks/useToggle';
import {
  PlayerSearchViewAttributes,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';
import { useShowBottomBarNav } from '@sorare/core/src/routing/MultiSportBottomNavBar/useShowBottomBarNav';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { PaginatedPlayerRows } from 'components/benchObject/PaginatedPlayerRows';
import { ActiveFilters } from 'search/ActiveFilters';
import FiltersManagerDialog from 'search/FiltersManagerDialog';
import SearchBox from 'search/SearchBox';
import ToggleFiltersManager from 'search/ToggleFiltersManager';
import { PlayerSearchHelp } from 'searchCommonPlayer/PlayerSearchHelp';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';
import { ToggleStatsManager } from 'searchCommonPlayer/ToggleStatsManager';

import { Segment } from '../Segment';
import { Props } from './types';

const NoSmoothScrollStyle = createGlobalStyle`
  html {
    /* overrides the default scroll behavior we have in global style.css */
    scroll-behavior: initial;
  }
`;

const Root = styled(Vertical).attrs({ gap: 3 })``;
const SearchLayoutMain = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;

const Header = styled(Vertical)`
  padding: var(--unit) var(--double-unit);
  @media ${tabletAndAbove} {
    padding: var(--unit) var(--triple-unit);
  }
`;
const Filters = styled(Horizontal).attrs({ gap: 1.5 })`
  justify-content: space-between;
  flex-shrink: 0;
  width: 100%;
`;

const SearchBoxCtn = styled.label`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  transition: 0.2s;
  overflow: hidden;
  &:focus-within {
    width: 240px;
  }
  @media ${laptopAndAbove} {
    width: 240px;
  }
`;
const Grid = styled.div`
  @media ${tabletAndAbove} {
    padding: 0 var(--triple-unit);
  }
`;
const StyledContentWithStickyHeader = styled(ContentWithStickyHeader)`
  width: 100%;
`;
const StyledFullViewportScroll = styled(FullViewportScroll)`
  width: 100%;
`;
const ToggleStatsManagerWrapper = styled.div`
  margin-bottom: var(--double-unit);
`;

export const messages = defineMessages({
  defaultPlaceholder: {
    id: 'AdvancedCardSearch.scoutingCenter',
    defaultMessage: 'Filter by player or team',
  },
});

export const PlayerSearchLayout = (props: Props) => {
  const bottomNavBar = useShowBottomBarNav();
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const {
    topic,
    favPlayerHit,
    cardFilters,
    sorts,
    children,
    PlayerDetailsComponent,
    sport,
    GridComponent = PaginatedPlayerRows,
    dropdownFilters,
  } = props;
  const [filtersOpen, toggleFilters] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const { refresh } = useInstantSearch();
  const { statsManager, sawOnboarding } = useManagePlayerSearchView();
  const shouldShowFiltersOnboarding =
    statsManager[PlayerSearchViewAttributes.ONBOARDING] ===
    StatsOnboarding.FILTERS;

  const searchBoxPlaceholder = topic
    ? {
        scoutingCenter: messages.defaultPlaceholder,
      }[topic.type]
    : messages.defaultPlaceholder;

  const searchLayout = (
    <Root>
      <NoSmoothScrollStyle />
      <SearchLayoutContainer>
        <StyledContentWithStickyHeader
          header={
            <Header>
              <ConditionalWrapper
                wrap={!isLaptopOrAbove}
                Wrapper={StyledFullViewportScroll}
              >
                <Filters>
                  <Horizontal>
                    <SearchBoxCtn>
                      <SearchBox
                        withClearIcon
                        placeholder={formatMessage(searchBoxPlaceholder, {
                          name: topic?.label,
                        })}
                        favPlayerHit={favPlayerHit}
                      />
                    </SearchBoxCtn>
                    <PlayerSearchHelp
                      step={StatsOnboarding.FILTERS}
                      placement="top"
                    >
                      <ToggleFiltersManager
                        color="quaternary"
                        toggleFilters={() => {
                          if (shouldShowFiltersOnboarding)
                            sawOnboarding(StatsOnboarding.FILTERS);
                          toggleFilters();
                        }}
                        size="small"
                      />
                    </PlayerSearchHelp>
                    <Segment dropdownFilters={dropdownFilters} />
                  </Horizontal>
                  {(isLaptopOrAbove || !bottomNavBar) && <ToggleStatsManager />}
                </Filters>
              </ConditionalWrapper>
              <ActiveFilters forceScroll />
            </Header>
          }
        >
          <SearchLayoutMain>
            {children}
            <Grid>
              <GridComponent
                topic={topic}
                PlayerDetailsComponent={PlayerDetailsComponent}
                sport={sport}
                indexes={sorts}
              />
            </Grid>
          </SearchLayoutMain>
        </StyledContentWithStickyHeader>
      </SearchLayoutContainer>
      {!isLaptopOrAbove && bottomNavBar && (
        <Portal id="above-bottom-bar-portal">
          <ToggleStatsManagerWrapper>
            <ToggleStatsManager />
          </ToggleStatsManagerWrapper>
        </Portal>
      )}
      <FiltersManagerDialog
        open={filtersOpen}
        onClose={toggleFilters}
        filtersGroups={cardFilters}
      />
    </Root>
  );
  if (isLaptopOrAbove) return searchLayout;
  return (
    <PullToRefetch refetch={async () => refresh()}>
      {searchLayout}
    </PullToRefetch>
  );
};
